import { components as c } from "@/components";
import InjectionDetailsFormFields from "@/features/injection/components/InjectionDetailsFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import Stepper from "@/components/common/Stepper";
import { useReplaceInjectionDetails } from "@/features/injection/api/useReplaceInjectionDetails";
import {
    formatInjectionDetailsDefaultValues,
    InjectionDetailSchemaT,
    InjectionDetailsZ,
} from "@/features/injection/components/InjectionDetailsForm";
import { extractInjectionById } from "@/features/injection/hooks/useExtractInjection";
import FormErrors from "@patient-watch/forms/FormErrors";
import { useEventStepper } from "@/features/templates/hooks/useEventNavigationStore";
import { useEffect } from "react";
import { diaryByIdQueryOptions } from "../../../../../../../../features/diaries";

const InjectionDetailsFormFieldsPage = () => {
    const { diaryId, eventId, injectionId } = Route.useParams();
    const { injection } = Route.useLoaderData();
    const navigate = useNavigate();

    const replaceInjectionDetails = useReplaceInjectionDetails({
        diaryId: diaryId,
    });

    const form = useForm<InjectionDetailSchemaT>({
        resolver: zodResolver(InjectionDetailsZ),
        defaultValues: formatInjectionDetailsDefaultValues(injectionId, injection),
    });

    useEffect(() => {
        console.log("injection", injection, formatInjectionDetailsDefaultValues(injectionId, injection));
        if (injection) {
            form.reset(formatInjectionDetailsDefaultValues(injectionId, injection));
        }
    }, [injection, injectionId, form]);

    const { steps } = useEventStepper("injection-details");

    const onSubmit: SubmitHandler<InjectionDetailSchemaT> = async (formData) => {
        replaceInjectionDetails.mutate(
            {
                injection_details: formData.injection_details.map((detail) => ({
                    ...detail,
                    side: !detail.side ? null : detail.side,
                })),
                injection_id: injectionId,
            },
            {
                onSuccess: () => {
                    navigate({
                        to: "/templates/diary/$diaryId/events/$eventId/complete",
                        params: { diaryId, eventId },
                    });
                },
            }
        );
    };

    return (
        <div className="">
            <Stepper steps={steps} />
            <c.ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full form">
                    {/* Hidden input for injection_id */}

                    <InjectionDetailsFormFields injectionId={injectionId ?? ""} />

                    <FormErrors />

                    {/* Navigation Buttons */}
                    <div className="flex justify-between gap-4 mt-8">
                        <c.ui.Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                                navigate({
                                    to: "/templates/diary/$diaryId/events/$eventId/injection/$injectionId",
                                    params: { diaryId, eventId, injectionId },
                                })
                            }
                        >
                            Previous
                        </c.ui.Button>
                        <c.fc.NextButton />
                    </div>
                </form>
            </c.ui.Form>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/events/$eventId/injection/$injectionId/details")({
    component: InjectionDetailsFormFieldsPage,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));

        const injection = extractInjectionById(diary, params.injectionId);
        return {
            diary,
            injection,
            injectionDetailId: params.injectionDetailId,
        };
    },
});
